import { Button } from "@tellescope/ui";
import { Link, useParams } from "react-router-dom";
import { addHours } from "date-fns/addHours";
import { addMinutes } from "date-fns/addMinutes";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { MonthViewEventCell } from "../event-cells";
import { getZonedDayStart, type EventsByDate } from "../../utils/get-events-by-date";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";

export interface WeekViewMultiDayRowProps {
  daysOfWeek: string[];
  startDate: Date;
  multiDayEventsByDate: EventsByDate;
  timezone: string;
}

const WeekViewMultiDayRowInner = ({
  daysOfWeek,
  startDate,
  multiDayEventsByDate,
  timezone,
}: WeekViewMultiDayRowProps) => {
  const { t } = useTranslation("calendar");
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const params = useParams();
  const viewId = params.viewId;

  return (
    <>
      <div className="border-b border-b-gray-200" />
      {daysOfWeek.map((day, i) => {
        const date = getZonedDayStart(addHours(startDate, i * 24 + 1), timezone);
        const timestampForEvents = date.getTime();
        const nextDayStartMs = getZonedDayStart(addHours(date, 25), timezone).getTime();
        const [year, month, dayOfMonth] = formatInTimeZone(date, timezone, "yyyy-M-d").split("-").map(Number);
        const eventsToDisplay = multiDayEventsByDate[timestampForEvents]?.slice(0, 3);
        const overflowEvents = multiDayEventsByDate[timestampForEvents]?.slice(3);

        return (
          <div key={`multi-day-row-${day}`} className="flex flex-col gap-1 py-1 border-b border-b-gray-200 min-w-0">
            {eventsToDisplay?.map((event) => {
              const eventStartDate = new Date(event.startTimeInMS);
              const eventEndDate = addMinutes(eventStartDate, event.actualDuration ?? event.durationInMinutes);
              const fromPrevious = eventStartDate.getTime() < timestampForEvents;
              const continuesToNext = eventEndDate.getTime() > nextDayStartMs;

              return (
                <MonthViewEventCell
                  key={`${event.id}-${timestampForEvents}`}
                  event={event}
                  fromPrevious={fromPrevious}
                  continuesToNext={continuesToNext}
                  onClick={{ to: `.?eventId=${event.id}`, replace: true }}
                  showTitleOverride={i === 0}
                  timezone={timezone}
                />
              );
            })}
            {!!overflowEvents?.length && (
              <Button asChild variant="ghost" size="sm" className="font-light text-xs h-auto py-1">
                <Link to={`/calendar/view/${viewId}/day/${year}/${(month ?? 1) - 1}/${dayOfMonth}`} replace>
                  {t("{{amount}} more...", { amount: overflowEvents?.length ?? 0 })}
                </Link>
              </Button>
            )}
          </div>
        );
      })}
    </>
  );
};

export const WeekViewMultiDayRow = memo(WeekViewMultiDayRowInner);
